

import { Fragment } from "react";
import { format, parseISO } from "date-fns";
import { ptBR, es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Topico } from "../../../services/topico.service";
import type { ItemAta } from "../../../services/itemAta.service";
import type { Desenvolvimento } from "../../../services/desenvolvimento.service";
import type { PdfDevelopmentsLimit } from "@/components/CustomizeFilenameDialog";

interface TopicPreviewSectionProps {
  topico: Topico;
  items: Array<ItemAta & { desenvolvimentos: Desenvolvimento[] }>;
  topicIndex: number;
  meetingNumber: number;
  developmentsLimit?: PdfDevelopmentsLimit;
}

export function TopicPreviewSection({
  topico,
  items,
  topicIndex,
  meetingNumber,
  developmentsLimit = null,
}: TopicPreviewSectionProps) {
  const { t, i18n } = useTranslation('common');
  
  const pendingItems = items
    .filter(item => !item.concluido)
    .sort((a, b) => {
      const na = a.numero_item || '';
      const nb = b.numero_item || '';
      const partsA = na.split('.').map(p => parseInt(p.replace(/\D/g, ''), 10) || 0);
      const partsB = nb.split('.').map(p => parseInt(p.replace(/\D/g, ''), 10) || 0);
      for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
        const diff = (partsA[i] || 0) - (partsB[i] || 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'es': return es;
      case 'en': return enUS;
      default: return ptBR;
    }
  };

  const formatTableDate = (rawDate?: string, locale = getDateLocale()) => {
    if (!rawDate) return "-";

    try {
      const dateStr = rawDate.includes("T") ? rawDate : `${rawDate}T00:00:00`;
      return format(parseISO(dateStr), "dd/MM/yy", { locale });
    } catch {
      return rawDate;
    }
  };
  
  if (pendingItems.length === 0) {
    return (
      <div className="page-break-inside-avoid mb-10">
        <div className="border-b border-gray-400 pb-0.5 mb-3">
          <h2 className="text-xl font-bold text-gray-900">
            {topicIndex + 1}. {topico.titulo.toUpperCase()}
          </h2>
        </div>
        <p className="text-sm text-gray-400 italic px-1">Nenhum item pendente.</p>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="page-break-inside-avoid border-b border-gray-400 pb-0.5 mb-3">
        <h2 className="text-xl font-bold text-gray-900">
          {topicIndex + 1}. {topico.titulo.toUpperCase()}
        </h2>
      </div>

      <div className="hidden md:block print:block">
        <Table>
          <TableHeader className="bg-gray-100">
            <TableRow className="bg-gradient-to-r from-gray-100 to-gray-200 border-b border-gray-400">
              <TableHead
                className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide"
                style={{ width: "5%" }}
              >
                {t('item')}
              </TableHead>
              <TableHead
                className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                style={{ width: "7%" }}
              >
                {t('minutes_number')}
              </TableHead>
              <TableHead
                className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                style={{ width: "10%", whiteSpace: "nowrap" }}
              >
                {t('date')}
              </TableHead>
              <TableHead
                className="text-left font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                style={{ width: "59%" }}
              >
                {t('subject_description')}
              </TableHead>
              <TableHead
                className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                style={{ width: "9%" }}
              >
                {t('responsible')}
              </TableHead>
              <TableHead
                className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                style={{ width: "10%" }}
              >
                {t('deadline')}
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {pendingItems.map((item, itemIdx) => {
              const isResolved = false;
              const itemDevs = item.desenvolvimentos.sort(
                (a, b) => a.ata_numero - b.ata_numero
              );
              const visibleDevs =
                developmentsLimit === null
                  ? itemDevs
                  : itemDevs.slice(-developmentsLimit);
              const hasMoreDevs =
                developmentsLimit !== null && itemDevs.length > developmentsLimit;
              const hiddenDevsCount =
                developmentsLimit !== null
                  ? Math.max(itemDevs.length - developmentsLimit, 0)
                  : 0;
              const itemCode = item.numero_item || `${topicIndex + 1}.${itemIdx + 1}`;
              const rowClassName = `${
                itemIdx % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
              } hover:bg-gray-200 transition-colors ${
                isResolved ? "opacity-60 print:opacity-100" : ""
              }`;
              const sideCellClassName =
                "text-center align-top font-semibold text-gray-700 border-l border-gray-300";

              return (
                <Fragment key={item.id}>
                  <TableRow className={rowClassName}>
                    <TableCell className="text-center align-top font-semibold text-gray-900 border-l border-gray-200">
                      {itemCode}
                    </TableCell>
                    <TableCell className={sideCellClassName}>
                      {item.ata_origem_numero ? `#${item.ata_origem_numero}` : "-"}
                    </TableCell>
                    <TableCell className={sideCellClassName} style={{ whiteSpace: "nowrap" }}>
                      {formatTableDate(item.ata_origem_data, getDateLocale())}
                    </TableCell>
                    <TableCell className="align-top border-l border-gray-300">
                      <p
                        className={`text-sm text-gray-900 ${
                          isResolved ? "line-through" : ""
                        }`}
                      >
                        {item.descricao}
                        {isResolved && (
                          <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded uppercase">
                            Concluído
                          </span>
                        )}
                      </p>
                    </TableCell>
                    <TableCell className={sideCellClassName}>
                      {item.entidade || "-"}
                    </TableCell>
                    <TableCell className={sideCellClassName} style={{ whiteSpace: "nowrap" }}>
                      {formatTableDate(item.prazo, getDateLocale())}
                    </TableCell>
                  </TableRow>

                  {hasMoreDevs && (
                    <TableRow className={rowClassName}>
                      <TableCell className="border-l border-gray-200" />
                      <TableCell className="border-l border-gray-300" />
                      <TableCell className="border-l border-gray-300" />
                      <TableCell className="page-break-inside-avoid align-top border-l border-t border-gray-200 pt-1.5">
                        <div className="text-xs text-gray-500 italic">
                          ... ({hiddenDevsCount} atualizações anteriores)
                        </div>
                      </TableCell>
                      <TableCell className="border-l border-gray-300" />
                      <TableCell className="border-l border-gray-300" />
                    </TableRow>
                  )}

                  {visibleDevs.map((dev, devIdx) => {
                    const isHighlighted = dev.ata_numero === meetingNumber;
                    const isFirstDev = !hasMoreDevs && devIdx === 0;
                    return (
                      <TableRow
                        key={dev.id}
                        className={`${rowClassName} page-break-inside-avoid`}
                      >
                        <TableCell className="border-l border-gray-200" />
                        <TableCell className="border-l border-gray-300" />
                        <TableCell className="border-l border-gray-300" />
                        <TableCell
                          className={`page-break-inside-avoid align-top border-l border-gray-300 pt-1.5 ${
                            isFirstDev ? "border-t border-t-gray-200" : ""
                          }`}
                        >
                          <div className="text-sm flex items-start gap-2">
                            {isHighlighted && (
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-bold flex-shrink-0">
                                NOVO
                              </span>
                            )}
                            {isHighlighted ? (
                              <strong className="text-gray-700 whitespace-pre-line">
                                A{dev.ata_numero} | {dev.texto}
                              </strong>
                            ) : (
                              <span className="text-gray-700 whitespace-pre-line">
                                A{dev.ata_numero} | {dev.texto}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="border-l border-gray-300" />
                        <TableCell className="border-l border-gray-300" />
                      </TableRow>
                    );
                  })}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

